/**
 * PDF.js Configuration
 * 
 * Central configuration for PDF.js across all LumeroFlow components.
 * Uses the locally bundled worker to avoid external CDN dependencies.
 * 
 * Components that use this config:
 * - PDFFormViewerWithAnnotations.js
 * - PDFFormViewer.js (if still used)
 * - Any PDF preview components
 */
import * as pdfjs from "pdfjs-dist";

// Version must match the installed pdfjs-dist package
export const PDF_VERSION = "4.0.379";

// Use local worker file served from public folder
// This avoids external CDN dependencies and version mismatches
const WORKER_URL = `${process.env.PUBLIC_URL || ""}/pdf.worker.min.mjs`;

// Initialize PDF.js worker
let workerInitialized = false;
let workerError = null;

export function initPdfWorker() {
  if (workerInitialized) return { success: true };
  
  try {
    pdfjs.GlobalWorkerOptions.workerSrc = WORKER_URL;
    workerInitialized = true;
    return { success: true };
  } catch (err) {
    workerError = err;
    console.error("[PDF Config] Failed to initialize PDF.js worker:", err);
    return { 
      success: false, 
      error: "PDF_WORKER_INIT_FAILED",
      message: "Failed to initialize PDF viewer"
    };
  }
}

// Get worker status
export function getWorkerStatus() {
  return {
    initialized: workerInitialized,
    error: workerError,
    workerUrl: WORKER_URL,
    version: PDF_VERSION
  };
}

// Load a PDF document with error handling
export async function loadPdfDocument(data) {
  // Ensure worker is initialized
  const initResult = initPdfWorker();
  if (!initResult.success) {
    throw new Error(initResult.error);
  }
  
  try {
    const loadingTask = pdfjs.getDocument({ data });
    const pdf = await loadingTask.promise;
    return pdf;
  } catch (err) {
    console.error("[PDF Config] Failed to load PDF document:", err);
    
    // Determine error type for safe display
    if (err.name === "MissingPDFException") {
      throw new Error("PDF_NOT_FOUND");
    } else if (err.name === "InvalidPDFException") {
      throw new Error("PDF_INVALID");
    } else if (err.message?.includes("worker")) {
      throw new Error("PDF_WORKER_LOAD_FAILED");
    } else {
      throw new Error("PDF_LOAD_FAILED");
    }
  }
}

// Safe error messages for display (no technical details exposed)
export const PDF_ERROR_MESSAGES = {
  PDF_WORKER_INIT_FAILED: "Unable to initialize the PDF viewer. Please refresh and try again.",
  PDF_WORKER_LOAD_FAILED: "Unable to load the PDF viewer. Please refresh and try again.",
  PDF_NOT_FOUND: "The PDF document could not be found.",
  PDF_INVALID: "The PDF document appears to be corrupted or invalid.",
  PDF_LOAD_FAILED: "Unable to load the PDF document. Please try again.",
  PDF_FLATTENING_FAILED: "Unable to generate the completed document. Please try again."
};

export function getSafeErrorMessage(errorCode) {
  return PDF_ERROR_MESSAGES[errorCode] || PDF_ERROR_MESSAGES.PDF_LOAD_FAILED;
}

// Re-export pdfjs for components that need direct access
export { pdfjs };
